import { Component, ChangeDetectionStrategy, input, output, computed, inject, signal } from '@angular/core';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { Estimate, VendorOrder } from '../../../../../../shared/models/vendor-order.model';
import { VendorService } from '../../../../../../shared/services/vendor.service';
import { QuoteSowModalComponent, SowModalData } from '../quote-sow-modal/quote-sow-modal.component';
import { VendorSelectBtnComponent } from '../../../../../../components/vendor-select-btn/vendor-select-btn.component';
import { DownloadLoadingDialogComponent } from '../../../../../../shared/components/download-loading-dialog/download-loading-dialog.component';

@Component({
  selector: 'app-quote-details',
  standalone: true,
  imports: [CurrencyPipe, DecimalPipe, VendorSelectBtnComponent],
  templateUrl: './quote-details.component.html',
  styleUrl: './quote-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuoteDetailsComponent {
  private readonly dialog = inject(MatDialog);
  private readonly vendorService = inject(VendorService);

  readonly estimate = input.required<Estimate>();
  readonly order = input<VendorOrder>();
  readonly isLowestRate = input(false);
  readonly back = output<void>();
  readonly quoteConfirmed = output<string>();

  readonly showSelectQuote = computed(() => {
    const est = this.estimate();
    return !!est.priceMax && est.jobStatus === 'AWAITING_VENDOR_SELECTION';
  });

  readonly loading = signal(false);

  readonly vendorName = computed(() => this.estimate().name ?? 'Unknown Vendor');

  readonly currency = computed(() => {
    const est = this.estimate();
    return est.currency ?? est.quote?.normal?.currency ?? 'USD';
  });

  readonly quoteId = computed(() => this.order()?.jobNumber ?? '--');

  readonly rows = computed(() => {
    const est = this.estimate();
    const result: { jobType: string; deliveryDate: string; quote: any }[] = [];

    if (est.quote?.normal) {
      const deliveryDate = est.estimatedDeliveryDates?.normal?.estimatedDeliveryDate ?? est.deadline;
      result.push({
        jobType: 'Standard',
        deliveryDate: deliveryDate ? this.formatDateDDMonYYYY(deliveryDate) : '--',
        quote: est.quote.normal,
      });
    }

    if (est.quote?.rushed) {
      const deliveryDate = est.estimatedDeliveryDates?.rushed?.estimatedDeliveryDate ?? est.deadline;
      result.push({
        jobType: 'Rushed',
        deliveryDate: deliveryDate ? this.formatDateDDMonYYYY(deliveryDate) : '--',
        quote: est.quote.rushed,
      });
    }

    if (!est.quote?.normal && !est.quote?.rushed) {
      const deliveryDate = est.deadline;
      result.push({
        jobType: 'Standard',
        deliveryDate: deliveryDate ? this.formatDateDDMonYYYY(deliveryDate) : '--',
        quote: null,
      });
    }

    return result;
  });

  getCostBreakdown(quote: any): { concept: string; cost: number }[] {
    if (!quote?.costs?.length) return [];
    return quote.costs;
  }

  getTotalCost(quote: any): number {
    if (quote?.totalCost != null) return quote.totalCost;
    const est = this.estimate();
    return est.priceMax ?? est.priceMin ?? 0;
  }

  getRecommendedPOTotal(quote: any): number {
    return Math.ceil(this.getTotalCost(quote) * 1.15 * 100) / 100;
  }

  async generateQuotePDF(): Promise<void> {
    const loadingRef = this.dialog.open(DownloadLoadingDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });

    const order = this.order();
    const estimate = this.estimate();

    let breakdown;
    try {
      breakdown = order?.jobCreatedIn === 'xtm'
        ? await this.vendorService.getXtmCostBreakdown(order.jobUid ?? '', estimate.costId ?? '', estimate.costBreakdownId ?? '')
        : await this.vendorService.getEstimateReportBreakdown(
            order?.contentType ?? '',
            order?.sourceLocaleId ?? '',
            order?.targetLocaleId?.join(',') ?? '',
            estimate.estimationsId ?? '',
          );
    } catch {
      breakdown = undefined;
    }

    let vendor;
    try {
      vendor = estimate.vendorId
        ? await this.vendorService.getVendorDetails(estimate.vendorId)
        : undefined;
    } catch {
      vendor = undefined;
    }

    const modalData: SowModalData = { order: order!, estimate, breakdown, vendor };

    const dialogRef = this.dialog.open(QuoteSowModalComponent, {
      data: modalData,
      width: '960px',
      maxWidth: '95vw',
      height: '65vh',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });

    await firstValueFrom(dialogRef.afterOpened());
    // Allow content to fully render before capturing
    await new Promise(resolve => setTimeout(resolve, 300));
    loadingRef.close();
  }

  async openSowModal(): Promise<void> {
    const order = this.order();
    const estimate = this.estimate();

    let breakdown;
    try {
      breakdown = order?.jobCreatedIn === 'xtm'
        ? await this.vendorService.getXtmCostBreakdown(order.jobUid ?? '', estimate.costId ?? '', estimate.costBreakdownId ?? '')
        : await this.vendorService.getEstimateReportBreakdown(
            order?.contentType ?? '',
            order?.sourceLocaleId ?? '',
            order?.targetLocaleId?.join(',') ?? '',
            estimate.estimationsId ?? '',
          );
    } catch {
      breakdown = undefined;
    }

    let vendor;
    try {
      vendor = estimate.vendorId
        ? await this.vendorService.getVendorDetails(estimate.vendorId)
        : undefined;
    } catch {
      vendor = undefined;
    }

    const modalData: SowModalData = { order: order!, estimate, breakdown, vendor };

    this.dialog.open(QuoteSowModalComponent, {
      data: modalData,
      width: '960px',
      maxWidth: '95vw',
      height: '65vh',
      panelClass: 'no-padding-dialog',
      disableClose: false,
    });
  }

  private formatDateDDMonYYYY(dateStr: string): string {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '--';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate().toString().padStart(2, '0');
    return `${day}/${months[date.getMonth()]}/${date.getFullYear()}`;
  }
  
}
